import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TelegramIcon from '@mui/icons-material/Telegram';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import useIsMobile from '../hooks/useIsMobile';

// ── Helpers ───────────────────────────────────────────────────────────────
const MONTHS_RU = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];
const NO_DATA_HINT =
  'Нет посещаемости и проверок за этот месяц. Администратор может подтянуть посещаемость из AlfaCRM в разделе Админ → AlfaCRM.';
const DATA_CONCURRENCY = 4;

function toMonthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(key) {
  const [y, m] = String(key).split('-').map(Number);
  return MONTHS_RU[m - 1] ? `${MONTHS_RU[m - 1]} ${y}` : key;
}
// Previous calendar month first, then further back.
function pastMonths(count) {
  const now = new Date();
  return Array.from({ length: count }, (_, i) =>
    toMonthKey(new Date(now.getFullYear(), now.getMonth() - 1 - i, 1))
  );
}
function fmtDateTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}
function idOf(v) {
  if (!v) return '';
  return String(typeof v === 'object' ? v._id || '' : v);
}
function asText(v) {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}
function asStrings(v) {
  return Array.isArray(v) ? v.map(asText).filter((s) => s.trim()) : [];
}
function pct(v) {
  return v == null || Number.isNaN(Number(v)) ? '—' : `${Math.round(v)}%`;
}

// Transliterate Cyrillic → Latin for safe filenames (Telegram + Windows).
const TRANSLIT = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};
function fileSafe(str, fallback) {
  const out = Array.from(String(str || ''))
    .map((ch) => {
      const r = TRANSLIT[ch.toLowerCase()];
      if (r === undefined) return ch;
      return ch === ch.toLowerCase() || !r
        ? r
        : r[0].toUpperCase() + r.slice(1);
    })
    .join('')
    .replace(/[^\w\s.-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return out || fallback;
}

async function readError(res) {
  const body = await res.json().catch(() => ({}));
  return body.message || `Ошибка ${res.status}`;
}

async function fetchMonthlyPdf(ids, lang) {
  const res = await fetch('/api/pdf/monthly-report', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ids, lang }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.blob();
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Runs `worker` over `items` with at most `limit` in flight.
async function runPool(items, limit, worker) {
  let next = 0;
  const lane = async () => {
    while (next < items.length) {
      const i = next++;
      await worker(items[i], i);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, lane)
  );
}

// Collapses GET /api/ai/monthly-data into the numbers shown on a row.
function summarizeMonthData(d) {
  const att = d?.attendance || {};
  const templates = Array.isArray(d?.templates) ? d.templates : [];
  const avg = (key) => {
    const vals = templates
      .map((t) => t?.[key])
      .filter((v) => v != null && !Number.isNaN(Number(v)))
      .map(Number);
    return vals.length
      ? Math.round(vals.reduce((s, v) => s + v, 0) / vals.length)
      : null;
  };
  return {
    total: Number(att.total) || 0,
    present: Number(att.present) || 0,
    late: Number(att.late) || 0,
    checks: templates.reduce(
      (s, t) => s + (Number(t?.assessmentsInPeriod) || 0),
      0
    ),
    newly: templates.reduce((s, t) => s + (Number(t?.newlyMastered) || 0), 0),
    startPct: avg('startPct'),
    endPct: avg('endPct'),
    templates,
    hasData: d?.hasData !== false,
  };
}

// ── Read-only report body (also used by the History tab) ─────────────────
function Block({ title, color = '#1e3a8a', children }) {
  return (
    <Box sx={{ mb: 1.75 }}>
      <Typography
        variant="caption"
        fontWeight={800}
        sx={{
          display: 'block',
          color,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Bullets({ items, color }) {
  return (
    <Stack spacing={0.5}>
      {items.map((t, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
          <Box
            sx={{
              mt: '0.6em',
              width: 5,
              height: 5,
              flexShrink: 0,
              borderRadius: '50%',
              bgcolor: color,
            }}
          />
          <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
            {t}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function StatsStrip({ stats }) {
  const att = stats?.attendance || {};
  const total = Number(att.total) || 0;
  const present = Number(att.present) || 0;
  const hasPct = stats?.startPct != null || stats?.endPct != null;
  const items = [
    total > 0 &&
      `Посещаемость: ${present} из ${total} (${Math.round((present / total) * 100)}%)`,
    stats?.assessmentsInPeriod != null &&
      `Проверок: ${Number(stats.assessmentsInPeriod) || 0}`,
    hasPct && `Освоение ${pct(stats.startPct)} → ${pct(stats.endPct)}`,
  ].filter(Boolean);
  if (!items.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
      {items.map((t) => (
        <Chip
          key={t}
          label={t}
          size="small"
          sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600 }}
        />
      ))}
    </Box>
  );
}

export function MonthlyReportPreview({ report, stats, letter }) {
  if (!report || typeof report !== 'object') return null;
  const covered = asStrings(report.covered);
  const strengths = asStrings(report.strengths);
  const focus = asStrings(report.nextMonthFocus);
  const newly = asStrings(stats?.newlyMastered);
  const improve = (Array.isArray(report.improve) ? report.improve : [])
    .map((x) =>
      typeof x === 'string'
        ? { area: x, howToPractice: '' }
        : { area: asText(x?.area), howToPractice: asText(x?.howToPractice) }
    )
    .filter((x) => x.area || x.howToPractice);
  const letterText = asText(letter);
  return (
    <Box>
      {stats && <StatsStrip stats={stats} />}
      {asText(report.summary) && (
        <Typography variant="body2" sx={{ mb: 1.75, lineHeight: 1.7 }}>
          {asText(report.summary)}
        </Typography>
      )}
      {asText(report.attendanceComment) && (
        <Block title="Посещаемость">
          <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
            {asText(report.attendanceComment)}
          </Typography>
        </Block>
      )}
      {covered.length > 0 && (
        <Block title="Что прошли" color="#0891b2">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {covered.map((c, i) => (
              <Chip
                key={i}
                label={c}
                size="small"
                sx={{
                  bgcolor: '#0891b218',
                  color: '#0e7490',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                }}
              />
            ))}
          </Box>
        </Block>
      )}
      {(asText(report.progress) || newly.length > 0) && (
        <Block title="Прогресс" color="#7c3aed">
          {asText(report.progress) && (
            <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
              {asText(report.progress)}
            </Typography>
          )}
          {newly.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.75,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Новое освоено:
              </Typography>
              {newly.map((w, i) => (
                <Chip
                  key={i}
                  label={w}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              ))}
            </Box>
          )}
        </Block>
      )}
      {strengths.length > 0 && (
        <Block title="Сильные стороны" color="#16a34a">
          <Bullets items={strengths} color="#16a34a" />
        </Block>
      )}
      {improve.length > 0 && (
        <Block title="Над чем поработать" color="#d97706">
          <Stack spacing={0.75}>
            {improve.map((x, i) => (
              <Box
                key={i}
                sx={{ pl: 1.25, borderLeft: '3px solid #fde68a', py: 0.25 }}
              >
                <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                  {x.area && <b>{x.area}</b>}
                  {x.area && x.howToPractice ? ' — ' : ''}
                  {x.howToPractice}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Block>
      )}
      {focus.length > 0 && (
        <Block title="Фокус на следующий месяц" color="#C2233E">
          <Bullets items={focus} color="#C2233E" />
        </Block>
      )}
      {asText(report.teacherNotes) && (
        <Box
          sx={{
            mb: 1.75,
            p: 1.25,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'grey.400',
            bgcolor: 'grey.50',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 0.5 }}
          >
            <Typography
              variant="caption"
              fontWeight={800}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
              Заметки
            </Typography>
            <Chip
              label="только для преподавателя"
              size="small"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.65 }}
          >
            {asText(report.teacherNotes)}
          </Typography>
        </Box>
      )}
      {letterText && (
        <Block title="Письмо родителям">
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75 }}
          >
            {letterText}
          </Typography>
        </Block>
      )}
    </Box>
  );
}

// ── One student row in the selection list ─────────────────────────────────
function StudentRow({
  student,
  first,
  checked,
  onToggle,
  info,
  hasReport,
  generatingNow,
  failure,
  disabled,
}) {
  const s = info?.data ? summarizeMonthData(info.data) : null;
  const name = student.name || info?.data?.studentName || 'Без имени';
  const pctTooltip =
    s && s.templates.length > 1
      ? s.templates
          .map(
            (t) =>
              `${asText(t?.name) || 'Шаблон'}: ${pct(t?.startPct)} → ${pct(t?.endPct)}`
          )
          .join('\n')
      : '';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 1.5,
        rowGap: 0.5,
        px: 1.5,
        py: 0.75,
        borderTop: first ? 'none' : '1px solid',
        borderColor: 'divider',
        opacity: onToggle && !checked ? 0.55 : 1,
      }}
    >
      {onToggle && (
        <Checkbox
          size="small"
          checked={checked}
          onChange={onToggle}
          disabled={disabled}
          sx={{ p: 0.5, ml: -0.5 }}
        />
      )}
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ flex: '1 1 140px', minWidth: 0 }}
        noWrap
      >
        {name}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 1.5,
          rowGap: 0.25,
          flex: '3 1 280px',
        }}
      >
        {!info || info.loading ? (
          <CircularProgress size={14} />
        ) : info.error ? (
          <Typography variant="caption" color="error">
            {info.error}
          </Typography>
        ) : (
          <>
            <Typography variant="caption" color="text.secondary">
              Занятий: {s.total} (был(а) {s.present}
              {s.late > 0 ? `, опозд. ${s.late}` : ''})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Проверок: {s.checks}
            </Typography>
            {(s.startPct != null || s.endPct != null) && (
              <Tooltip
                title={
                  pctTooltip ? (
                    <span style={{ whiteSpace: 'pre-line' }}>{pctTooltip}</span>
                  ) : (
                    ''
                  )
                }
              >
                <Typography variant="caption" color="text.secondary">
                  Освоение {pct(s.startPct)} → {pct(s.endPct)}
                  {s.newly > 0 ? ` (+${s.newly})` : ''}
                </Typography>
              </Tooltip>
            )}
            {!s.hasData && (
              <Tooltip title={NO_DATA_HINT}>
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<WarningAmberIcon />}
                  label="Нет данных за месяц"
                  sx={{ height: 20, fontSize: '0.68rem' }}
                />
              </Tooltip>
            )}
          </>
        )}
      </Box>
      <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
        {generatingNow && (
          <Chip
            size="small"
            icon={<CircularProgress size={10} color="inherit" />}
            label="Пишется…"
            color="primary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.68rem' }}
          />
        )}
        {failure && (
          <Tooltip title={failure}>
            <Chip
              size="small"
              color="error"
              label="Ошибка"
              sx={{ height: 20, fontSize: '0.68rem' }}
            />
          </Tooltip>
        )}
        {hasReport && (
          <Chip
            size="small"
            color="success"
            icon={<CheckIcon />}
            label="Готов"
            sx={{ height: 20, fontSize: '0.68rem' }}
          />
        )}
      </Stack>
    </Box>
  );
}

// ── One generated report (accordion) ──────────────────────────────────────
function ReportCard({
  item,
  name,
  draft,
  onDraftChange,
  busy,
  defaultExpanded,
  onSave,
  onSend,
  onPdf,
  onDelete,
}) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(!!defaultExpanded);
  const sc = item.structuredContent || null;
  const report = sc?.report || null;
  const stats = sc?.stats || null;
  const saved = asText(item.content) || asText(report?.parentLetter);
  const text = draft != null ? draft : saved;
  const dirty = draft != null && draft !== saved;
  const isSent = item.status === 'sent';
  const att = stats?.attendance || {};

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const spinner = <CircularProgress size={14} color="inherit" />;

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, v) => setExpanded(v)}
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{
        mb: 1.5,
        border: '1px solid',
        borderColor: isSent ? 'grey.200' : 'primary.100',
        borderRadius: '12px !important',
        overflow: 'hidden',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {name}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <Chip
              size="small"
              label={
                isSent ? `✓ Отправлено ${fmtDateTime(item.sentAt)}` : 'Черновик'
              }
              color={isSent ? 'success' : 'primary'}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
            {(dirty || item.edited) && (
              <Chip
                size="small"
                variant="outlined"
                label={dirty ? 'Не сохранено' : 'Письмо изменено'}
                color={dirty ? 'warning' : 'default'}
                sx={{ height: 20, fontSize: '0.7rem' }}
              />
            )}
            {Number(att.total) > 0 && (
              <Typography variant="caption" color="text.secondary">
                Занятий {Number(att.present) || 0}/{Number(att.total)}
              </Typography>
            )}
            {stats && (stats.startPct != null || stats.endPct != null) && (
              <Typography variant="caption" color="text.secondary">
                Освоение {pct(stats.startPct)} → {pct(stats.endPct)}
              </Typography>
            )}
            <Typography variant="caption" color="text.disabled">
              {fmtDateTime(item.createdAt)}
            </Typography>
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        {report ? (
          <MonthlyReportPreview report={report} stats={stats} />
        ) : (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
            Письмо изменено вручную — PDF и Telegram используют ваш текст.
          </Alert>
        )}
        <Block title="Письмо родителям">
          <TextField
            multiline
            fullWidth
            minRows={6}
            maxRows={24}
            value={text}
            onChange={(e) => onDraftChange(e.target.value)}
            placeholder="Текст письма для родителей…"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'grey.50',
                borderRadius: 2,
                '& textarea': { fontSize: 14, lineHeight: 1.7 },
                '&.Mui-focused': { bgcolor: 'white' },
              },
            }}
          />
        </Block>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button
            size="small"
            variant="contained"
            startIcon={busy === 'save' ? spinner : <SaveIcon />}
            disabled={!dirty || !!busy}
            onClick={onSave}
          >
            Сохранить
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={
              copied ? <CheckIcon color="success" /> : <ContentCopyIcon />
            }
            onClick={handleCopy}
            disabled={!text}
          >
            Копировать
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={busy === 'send' ? spinner : <TelegramIcon />}
            disabled={!!busy}
            onClick={onSend}
          >
            В Telegram (чат преподавателей)
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={busy === 'pdf' ? spinner : <PictureAsPdfIcon />}
            disabled={!!busy}
            onClick={onPdf}
          >
            PDF
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            size="small"
            color="error"
            startIcon={busy === 'delete' ? spinner : <DeleteIcon />}
            disabled={!!busy}
            onClick={onDelete}
          >
            Удалить
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

// ── Monthly report tab ────────────────────────────────────────────────────
export default function MonthlyReportTab({ groups, onGenerated, onNotify }) {
  const isMobile = useIsMobile();
  const months = useMemo(() => pastMonths(24), []);
  const [month, setMonth] = useState(months[0]);
  const [lang, setLang] = useState('ru');
  const [scope, setScope] = useState('group');
  const [groupId, setGroupId] = useState('');
  const [excluded, setExcluded] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [student, setStudent] = useState(null);
  const [dataById, setDataById] = useState({});
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState('');
  const [skipReady, setSkipReady] = useState(true);
  const [gen, setGen] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [failures, setFailures] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState({});
  const [bulkPdf, setBulkPdf] = useState(false);
  const [sendTarget, setSendTarget] = useState(null);
  const [sendWithPdf, setSendWithPdf] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const stopRef = useRef(false);
  const aliveRef = useRef(true);
  const reportsReq = useRef(0);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const running = !!gen;
  const notify = (message, severity = 'success') =>
    onNotify?.(message, severity);

  const group = groups.find((g) => g._id === groupId) || null;

  // Students the tab works with: the group's roster, or the one picked student.
  const roster = useMemo(() => {
    if (scope === 'student') {
      return student ? [{ _id: idOf(student), name: student.name || '' }] : [];
    }
    return (group?.studentIds || [])
      .map((s) =>
        s && typeof s === 'object'
          ? { _id: idOf(s), name: s.name || '' }
          : { _id: idOf(s), name: '' }
      )
      .filter((s) => s._id);
  }, [scope, student, group]);

  const selected =
    scope === 'group'
      ? roster.filter((s) => !excluded.includes(s._id))
      : roster;

  // Student list for the single-student picker (loaded on first use).
  useEffect(() => {
    if (scope !== 'student' || students.length) return;
    setStudentsLoading(true);
    fetch('/api/students', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setStudents(Array.isArray(d) ? d : []))
      .catch(() => setStudents([]))
      .finally(() => setStudentsLoading(false));
  }, [scope, students.length]);

  // Per-student "what data exists for this month" preview (no AI).
  useEffect(() => {
    let cancelled = false;
    setDataById(
      Object.fromEntries(roster.map((s) => [s._id, { loading: true }]))
    );
    runPool(roster, DATA_CONCURRENCY, async (s) => {
      if (cancelled) return;
      let entry;
      try {
        const res = await fetch(
          `/api/ai/monthly-data?month=${month}&studentId=${encodeURIComponent(s._id)}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error(await readError(res));
        entry = { data: await res.json() };
      } catch (e) {
        entry = { error: e.message || 'Не удалось загрузить данные' };
      }
      if (!cancelled) setDataById((prev) => ({ ...prev, [s._id]: entry }));
    });
    return () => {
      cancelled = true;
    };
  }, [month, roster]);

  // Reports that already exist for this month and scope.
  const loadReports = useCallback(async () => {
    const req = ++reportsReq.current;
    const query =
      scope === 'group'
        ? groupId && `groupId=${encodeURIComponent(groupId)}`
        : student && `studentId=${encodeURIComponent(idOf(student))}`;
    setReportsError('');
    if (!query) {
      setReports([]);
      setReportsLoading(false);
      return;
    }
    setReportsLoading(true);
    try {
      const res = await fetch(
        `/api/ai/monthly-report?month=${month}&${query}`,
        {
          credentials: 'include',
        }
      );
      if (!res.ok) throw new Error(await readError(res));
      const body = await res.json();
      if (req === reportsReq.current)
        setReports(Array.isArray(body) ? body : []);
    } catch (e) {
      if (req === reportsReq.current) {
        setReports([]);
        setReportsError(e.message || 'Не удалось загрузить отчёты');
      }
    } finally {
      if (req === reportsReq.current) setReportsLoading(false);
    }
  }, [month, scope, groupId, student]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // Newest report per student (regenerating creates a new doc).
  const latestByStudent = useMemo(() => {
    const map = {};
    reports.forEach((r) => {
      const sid = idOf(r.studentId);
      if (!sid) return;
      const prev = map[sid];
      if (!prev || new Date(r.createdAt) > new Date(prev.createdAt)) {
        map[sid] = r;
      }
    });
    return map;
  }, [reports]);

  const selectedReports = selected
    .map((s) => latestByStudent[s._id])
    .filter(Boolean);
  const toGenerate = selected.filter(
    (s) => !(skipReady && latestByStudent[s._id])
  );
  const failureById = Object.fromEntries(
    failures.map((f) => [f._id, f.message])
  );
  const nameOf = (r) =>
    roster.find((s) => s._id === idOf(r.studentId))?.name ||
    r.studentName ||
    asText(r.studentId?.name) ||
    'Ученик';

  const resetRun = () => {
    setFailures([]);
  };

  // ── Generation: one POST at a time (rate limits), keep going on errors ──
  const runGeneration = async (list) => {
    if (!list.length || running) return;
    stopRef.current = false;
    setStopping(false);
    setFailures([]);
    const failed = [];
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      if (stopRef.current || !aliveRef.current) break;
      const s = list[i];
      setGen({
        done: i,
        total: list.length,
        current: s.name || 'ученик',
        currentId: s._id,
      });
      try {
        const res = await fetch('/api/ai/monthly-report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ month, studentId: s._id, language: lang }),
        });
        if (!res.ok) throw new Error(await readError(res));
        const doc = await res.json();
        ok += 1;
        setReports((prev) => [doc, ...prev]);
      } catch (e) {
        failed.push({
          _id: s._id,
          name: s.name,
          message: e.message || 'Ошибка',
        });
      }
    }
    if (!aliveRef.current) return;
    const stopped = stopRef.current;
    setGen(null);
    setStopping(false);
    setFailures(failed);
    if (ok > 0) onGenerated?.();
    if (failed.length) {
      notify(`Готово: ${ok}, с ошибкой: ${failed.length}`, 'warning');
    } else {
      notify(
        stopped ? `Остановлено. Готово отчётов: ${ok}` : `Готово отчётов: ${ok}`
      );
    }
  };

  // ── Per-report actions ────────────────────────────────────────────────
  const setBusyFor = (id, value) =>
    setBusy((prev) => ({ ...prev, [id]: value }));

  const replaceReport = (doc) =>
    setReports((prev) => prev.map((r) => (r._id === doc._id ? doc : r)));

  const savedLetter = (item) =>
    asText(item.content) ||
    asText(item.structuredContent?.report?.parentLetter);
  const hasUnsaved = (item) =>
    drafts[item._id] != null && drafts[item._id] !== savedLetter(item);

  // Saves the edited parent letter (if any) and returns the server's doc.
  const persistDraft = async (item) => {
    if (!hasUnsaved(item)) return item;
    const draft = drafts[item._id];
    const res = await fetch(`/api/ai/content/${item._id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ content: draft }),
    });
    if (!res.ok) throw new Error(await readError(res));
    const doc = await res.json();
    replaceReport(doc);
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[item._id];
      return next;
    });
    return doc;
  };

  const handleSave = async (item) => {
    setBusyFor(item._id, 'save');
    try {
      await persistDraft(item);
      onGenerated?.();
      notify('Письмо сохранено');
    } catch (e) {
      notify(e.message || 'Не удалось сохранить', 'error');
    } finally {
      setBusyFor(item._id, null);
    }
  };

  const pdfName = (item) =>
    `Otchet_${fileSafe(nameOf(item), 'student')}_${month}.pdf`;

  const handlePdf = async (item) => {
    setBusyFor(item._id, 'pdf');
    try {
      const current = await persistDraft(item);
      const blob = await fetchMonthlyPdf(
        [current._id],
        current.language || lang
      );
      saveBlob(blob, pdfName(current));
    } catch (e) {
      notify(e.message || 'Не удалось создать PDF', 'error');
    } finally {
      setBusyFor(item._id, null);
    }
  };

  const handleConfirmSend = async () => {
    const item = sendTarget;
    const withPdf = sendWithPdf;
    setSendTarget(null);
    if (!item) return;
    setBusyFor(item._id, 'send');
    try {
      // Unsaved letter edits go to the server first.
      const current = await persistDraft(item);
      const fd = new FormData();
      if (withPdf) {
        const blob = await fetchMonthlyPdf(
          [current._id],
          current.language || lang
        );
        fd.append(
          'files',
          new File([blob], pdfName(current), { type: 'application/pdf' })
        );
      }
      const res = await fetch(`/api/ai/content/${current._id}/send`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error(await readError(res));
      const doc = await res.json().catch(() => null);
      if (doc?._id) replaceReport(doc);
      onGenerated?.();
      notify('Отправлено в Telegram (чат преподавателей)');
    } catch (e) {
      notify(e.message || 'Ошибка отправки', 'error');
    } finally {
      setBusyFor(item._id, null);
    }
  };

  const handleConfirmDelete = async () => {
    const item = deleteTarget;
    setDeleteTarget(null);
    if (!item) return;
    setBusyFor(item._id, 'delete');
    try {
      const res = await fetch(`/api/ai/content/${item._id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await readError(res));
      setReports((prev) => prev.filter((r) => r._id !== item._id));
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });
      onGenerated?.();
      notify('Отчёт удалён');
    } catch (e) {
      notify(e.message || 'Не удалось удалить', 'error');
    } finally {
      setBusyFor(item._id, null);
    }
  };

  const handleDownloadAll = async () => {
    if (!selectedReports.length) return;
    setBulkPdf(true);
    try {
      const ids = [];
      for (const r of selectedReports) {
        const current = await persistDraft(r);
        ids.push(current._id);
      }
      const blob = await fetchMonthlyPdf(ids, lang);
      const base =
        scope === 'group' && group
          ? `Otchety_${fileSafe(group.name, 'group')}`
          : `Otchet_${fileSafe(selected[0]?.name, 'student')}`;
      saveBlob(blob, `${base}_${month}.pdf`);
    } catch (e) {
      notify(e.message || 'Не удалось создать PDF', 'error');
    } finally {
      setBulkPdf(false);
    }
  };

  const allChecked = roster.length > 0 && excluded.length === 0;
  const someChecked = excluded.length > 0 && excluded.length < roster.length;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        AI напишет для каждого ученика отчёт для родителей за месяц:
        посещаемость, что прошли, прогресс по проверкам и что потренировать
        дома.
      </Typography>

      {/* Month + language */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 0.75 }}
      >
        <FormControl size="small" sx={{ minWidth: 220 }} disabled={running}>
          <InputLabel>Месяц</InputLabel>
          <Select
            value={month}
            label="Месяц"
            onChange={(e) => {
              setMonth(e.target.value);
              resetRun();
            }}
            startAdornment={
              <CalendarMonthIcon
                sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }}
              />
            }
            MenuProps={{ slotProps: { paper: { sx: { maxHeight: 360 } } } }}
          >
            {months.map((m) => (
              <MenuItem key={m} value={m}>
                {monthLabel(m)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 170 }} disabled={running}>
          <InputLabel>Язык письма</InputLabel>
          <Select
            value={lang}
            label="Язык письма"
            onChange={(e) => setLang(e.target.value)}
          >
            <MenuItem value="ru">Русский</MenuItem>
            <MenuItem value="en">English</MenuItem>
          </Select>
        </FormControl>
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 2 }}
      >
        Отчёт строится только по данным за выбранный месяц — можно сделать отчёт
        за прошлый месяц задним числом.
      </Typography>

      {/* Scope */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={scope}
        disabled={running}
        onChange={(_, v) => {
          if (!v) return;
          setScope(v);
          resetRun();
        }}
        sx={{
          mb: 1.5,
          '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 },
        }}
      >
        <ToggleButton value="group">
          <GroupIcon fontSize="small" sx={{ mr: 0.75 }} />
          Группа
        </ToggleButton>
        <ToggleButton value="student">
          <PersonIcon fontSize="small" sx={{ mr: 0.75 }} />
          Один ученик
        </ToggleButton>
      </ToggleButtonGroup>

      {scope === 'group' ? (
        <FormControl fullWidth size="small" sx={{ mb: 2 }} disabled={running}>
          <InputLabel>Выберите группу</InputLabel>
          <Select
            value={groupId}
            label="Выберите группу"
            onChange={(e) => {
              setGroupId(e.target.value);
              setExcluded([]);
              resetRun();
            }}
            startAdornment={
              <GroupIcon
                sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }}
              />
            }
          >
            {groups.map((g) => (
              <MenuItem key={g._id} value={g._id}>
                {g.name}
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {g.studentIds?.length || 0} учеников
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        <Autocomplete
          size="small"
          sx={{ mb: 2 }}
          disabled={running}
          options={students}
          loading={studentsLoading}
          value={student}
          onChange={(_, v) => {
            setStudent(v);
            resetRun();
          }}
          getOptionLabel={(o) => o?.name || ''}
          isOptionEqualToValue={(o, v) => idOf(o) === idOf(v)}
          renderOption={(props, o) => {
            const { key, ...rest } = props;
            return (
              <li key={idOf(o) || key} {...rest}>
                {o.name}
              </li>
            );
          }}
          noOptionsText="Ученики не найдены"
          loadingText="Загрузка…"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Ученик"
              placeholder="Начните вводить имя"
            />
          )}
        />
      )}

      {/* Students + data preview */}
      {scope === 'group' && group && roster.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          В этой группе нет учеников.
        </Alert>
      )}
      {roster.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            mb: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {scope === 'group' && (
              <Checkbox
                size="small"
                checked={allChecked}
                indeterminate={someChecked}
                disabled={running}
                onChange={() =>
                  setExcluded(allChecked ? roster.map((s) => s._id) : [])
                }
                sx={{ p: 0.5, ml: -0.5 }}
              />
            )}
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ flexGrow: 1 }}
            >
              {scope === 'group'
                ? `Ученики: выбрано ${selected.length} из ${roster.length}`
                : 'Данные за месяц'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {monthLabel(month)}
            </Typography>
          </Box>
          {roster.map((s, i) => (
            <StudentRow
              key={s._id}
              student={s}
              first={i === 0}
              checked={!excluded.includes(s._id)}
              onToggle={
                scope === 'group'
                  ? () =>
                      setExcluded((prev) =>
                        prev.includes(s._id)
                          ? prev.filter((id) => id !== s._id)
                          : [...prev, s._id]
                      )
                  : null
              }
              info={dataById[s._id]}
              hasReport={!!latestByStudent[s._id]}
              generatingNow={gen?.currentId === s._id}
              failure={failureById[s._id]}
              disabled={running}
            />
          ))}
        </Paper>
      )}

      {/* Generate */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1 }}
      >
        <Button
          variant="contained"
          disabled={running || toGenerate.length === 0}
          onClick={() => runGeneration(toGenerate)}
          startIcon={
            running ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AutoAwesomeIcon />
            )
          }
        >
          Сгенерировать отчёты ({toGenerate.length})
        </Button>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={skipReady}
              disabled={running}
              onChange={(e) => setSkipReady(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Пропускать готовые</Typography>}
        />
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 2 }}
      >
        {selected.length > 0 && toGenerate.length === 0
          ? 'Все выбранные отчёты уже готовы — снимите «Пропускать готовые», чтобы написать заново.'
          : 'Отчёты пишутся по одному, примерно 15–40 секунд на ученика.'}
      </Typography>

      {gen && (
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.100',
            bgcolor: 'primary.50',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
              AI пишет отчёт: {gen.done + 1} из {gen.total} — {gen.current}
            </Typography>
            <Button
              size="small"
              disabled={stopping}
              onClick={() => {
                stopRef.current = true;
                setStopping(true);
              }}
            >
              {stopping ? 'Останавливаю…' : 'Остановить'}
            </Button>
          </Stack>
          <LinearProgress
            variant="buffer"
            value={(gen.done / gen.total) * 100}
            valueBuffer={((gen.done + 1) / gen.total) * 100}
            sx={{ height: 6, borderRadius: 1 }}
          />
        </Paper>
      )}

      {!running && failures.length > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => runGeneration(failures)}
            >
              Повторить неудачные
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            Не удалось сгенерировать ({failures.length}):
          </Typography>
          {failures.map((f) => (
            <Typography key={f._id} variant="caption" sx={{ display: 'block' }}>
              {f.name || 'Ученик'} — {f.message}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Results */}
      {(selected.length > 0 || reportsLoading || reportsError) && (
        <Box sx={{ mt: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 1.5 }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ flexGrow: 1 }}
            >
              Отчёты за {monthLabel(month)} ({selectedReports.length})
            </Typography>
            <Tooltip title="Обновить">
              <span>
                <IconButton
                  size="small"
                  onClick={loadReports}
                  disabled={reportsLoading || running}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!selectedReports.length || bulkPdf}
              onClick={handleDownloadAll}
              startIcon={
                bulkPdf ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PictureAsPdfIcon />
                )
              }
            >
              Скачать PDF ({selectedReports.length})
            </Button>
          </Stack>
          {reportsError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {reportsError}
            </Alert>
          )}
          {reportsLoading && !reports.length ? (
            <Typography variant="body2" color="text.secondary">
              Загрузка...
            </Typography>
          ) : selectedReports.length === 0 && !reportsError ? (
            <Typography variant="body2" color="text.secondary">
              Пока нет готовых отчётов за этот месяц.
            </Typography>
          ) : (
            selectedReports.map((r) => (
              <ReportCard
                key={r._id}
                item={r}
                name={nameOf(r)}
                draft={drafts[r._id]}
                onDraftChange={(v) =>
                  setDrafts((prev) => ({ ...prev, [r._id]: v }))
                }
                busy={busy[r._id] || null}
                defaultExpanded={scope === 'student'}
                onSave={() => handleSave(r)}
                onSend={() => {
                  setSendWithPdf(true);
                  setSendTarget(r);
                }}
                onPdf={() => handlePdf(r)}
                onDelete={() => setDeleteTarget(r)}
              />
            ))
          )}
        </Box>
      )}

      {/* Telegram confirm */}
      <Dialog
        open={!!sendTarget}
        onClose={() => setSendTarget(null)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Отправить в чат преподавателей?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Письмо родителям ({sendTarget ? nameOf(sendTarget) : ''}) уйдёт в
            Telegram-группу учителей через бота Cherry-Pick Core — родителям
            напрямую оно не отправляется.
          </Typography>
          {sendTarget && hasUnsaved(sendTarget) && (
            <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
              Несохранённые правки будут сохранены перед отправкой.
            </Alert>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={sendWithPdf}
                onChange={(e) => setSendWithPdf(e.target.checked)}
                color="error"
                size="small"
              />
            }
            label={
              <Typography variant="body2">Прикрепить PDF-отчёт</Typography>
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendTarget(null)}>Отмена</Button>
          <Button
            variant="contained"
            startIcon={<TelegramIcon />}
            onClick={handleConfirmSend}
          >
            Отправить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Удалить отчёт?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deleteTarget
              ? `${nameOf(deleteTarget)} — ${monthLabel(month)}`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Отмена</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
